/**
 * Chart renderer component that selects and renders the appropriate chart type.
 * Wraps Recharts components and handles common concerns like loading and errors.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type {
  ChartConfig,
  ChartMarkEvent,
  ChartReferenceLine,
  TransformedChartData,
} from '../types/chartTypes';
import { normalizeRechartsClick, resolveMarkEvent } from '../utils/markEventResolution';
import {
  BarChartView,
  LineChartView,
  PieChartView,
  AreaChartView,
  ScatterChartView,
} from './charts';

export type ChartRendererProps = {
  data: TransformedChartData;
  config: ChartConfig;
  height?: number | `${number}%`;
  className?: string;
  /** Recharts default is true; dashboard tiles animate only on first mount. */
  isAnimationActive?: boolean;
  /**
   * Recharts ResponsiveContainer resize debounce (ms). 0 (default) matches the
   * standalone charts; dashboard tiles pass ~150 so a tile resize-drag doesn't
   * re-measure/re-render the chart on every frame (D6 UI Quality Contract).
   */
  resizeDebounceMs?: number;
  /** Target/goal lines for cartesian charts (dashboard tiles). */
  referenceLines?: ChartReferenceLine[];
  /**
   * Mark-level interactions (S11 cross-filter/drill). Single-click fires
   * after a 250ms window without a second click on the same mark; a second
   * click within the window fires onMarkDblClick instead.
   */
  onMarkClick?: (event: ChartMarkEvent) => void;
  onMarkDblClick?: (event: ChartMarkEvent) => void;
};

const DOUBLE_CLICK_WINDOW_MS = 250;

const sameMark = (a: ChartMarkEvent, b: ChartMarkEvent): boolean =>
  a.field === b.field && a.value === b.value && a.seriesValue === b.seriesValue;

export type ChartRendererHandle = {
  /** Gets the SVG content for export */
  getSvgContent: () => string | null;
};

/**
 * Inline computed styles from the live SVG onto cloned elements so they
 * render correctly when embedded standalone (outside the app's CSS context).
 * Covers text (fill, font), rects/paths (transforms), and other elements.
 */
function inlineComputedStyles(liveSvg: SVGSVGElement, clonedSvg: SVGSVGElement) {
  // Text elements: fonts and fills
  const liveTexts = liveSvg.querySelectorAll('text');
  const clonedTexts = clonedSvg.querySelectorAll('text');
  for (let i = 0; i < liveTexts.length && i < clonedTexts.length; i++) {
    const computed = getComputedStyle(liveTexts[i]);
    const clone = clonedTexts[i];
    clone.setAttribute('fill', computed.fill || '#666');
    clone.style.fontSize = computed.fontSize;
    clone.style.fontFamily = computed.fontFamily;
  }

  // Pie slices separate with a `var(--card)` stroke so the seam matches the
  // surface in both themes. A CSS var can't resolve outside the app, so bake
  // the computed color in for standalone/PDF rendering.
  const liveSectors = liveSvg.querySelectorAll('.recharts-sector');
  const clonedSectors = clonedSvg.querySelectorAll('.recharts-sector');
  for (let i = 0; i < liveSectors.length && i < clonedSectors.length; i++) {
    const computed = getComputedStyle(liveSectors[i]);
    if (computed.stroke && computed.stroke !== 'none') {
      clonedSectors[i].setAttribute('stroke', computed.stroke);
    }
  }

  // All elements: inline any CSS transforms and opacity that might
  // override attribute-based positioning (Recharts animation artifacts)
  const liveAll = liveSvg.querySelectorAll('*');
  const clonedAll = clonedSvg.querySelectorAll('*');
  for (let i = 0; i < liveAll.length && i < clonedAll.length; i++) {
    const live = liveAll[i] as SVGElement | HTMLElement;
    const clone = clonedAll[i] as SVGElement | HTMLElement;
    const computed = getComputedStyle(live);

    // Inline transforms if present (animation may set these via style)
    if (computed.transform && computed.transform !== 'none') {
      clone.style.transform = computed.transform;
    }
    // Inline opacity if not fully opaque
    if (computed.opacity && computed.opacity !== '1') {
      clone.style.opacity = computed.opacity;
    }
  }
}

/**
 * Renders the appropriate chart based on config.type.
 * Exposes a ref for accessing SVG content for export.
 */
export const ChartRenderer = forwardRef<ChartRendererHandle, ChartRendererProps>(
  function ChartRenderer(
    { data, config, height = 400, className, isAnimationActive, resizeDebounceMs, referenceLines, onMarkClick, onMarkDblClick },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);

    const pendingMarkRef = useRef<{ event: ChartMarkEvent; timer: ReturnType<typeof setTimeout> } | null>(null);
    useEffect(
      () => () => {
        if (pendingMarkRef.current) clearTimeout(pendingMarkRef.current.timer);
      },
      [],
    );

    // Views hand back raw Recharts click args; normalize + resolve here so
    // every chart type shares one contract and one single/double window.
    const handleRawMark = useCallback(
      (...args: unknown[]) => {
        const event = resolveMarkEvent(config, data, normalizeRechartsClick(...args));
        if (!event) return;
        const pending = pendingMarkRef.current;
        if (pending && sameMark(pending.event, event)) {
          clearTimeout(pending.timer);
          pendingMarkRef.current = null;
          (onMarkDblClick ?? onMarkClick)?.(event);
          return;
        }
        if (pending) {
          // Different mark: flush the pending single-click immediately.
          clearTimeout(pending.timer);
          pendingMarkRef.current = null;
          onMarkClick?.(pending.event);
        }
        if (!onMarkDblClick) {
          onMarkClick?.(event);
          return;
        }
        pendingMarkRef.current = {
          event,
          timer: setTimeout(() => {
            pendingMarkRef.current = null;
            onMarkClick?.(event);
          }, DOUBLE_CLICK_WINDOW_MS),
        };
      },
      [config, data, onMarkClick, onMarkDblClick],
    );
    const onMark = onMarkClick || onMarkDblClick ? handleRawMark : undefined;

    useImperativeHandle(ref, () => ({
      getSvgContent: () => {
        if (!containerRef.current) return null;
        // Recharts renders: ResponsiveContainer > .recharts-wrapper > svg.recharts-surface
        // IMPORTANT: Legend icons are also svg.recharts-surface (14x14) and may appear
        // first in DOM order when Legend is at the top. Target the main chart SVG
        // which is a direct child of .recharts-wrapper.
        const svg = containerRef.current.querySelector('.recharts-wrapper > svg.recharts-surface') as SVGSVGElement | null
          ?? containerRef.current.querySelector('svg.recharts-surface') as SVGSVGElement | null
          ?? containerRef.current.querySelector('svg') as SVGSVGElement | null;
        if (!svg || svg.width?.baseVal?.value < 50) return null; // skip tiny legend icons

        // Get the actual rendered dimensions before cloning
        const rect = svg.getBoundingClientRect();
        const w = Math.round(rect.width) || svg.width?.baseVal?.value || 800;
        const h = Math.round(rect.height) || svg.height?.baseVal?.value || 300;

        // Clone the SVG to avoid modifying the original
        const clonedSvg = svg.cloneNode(true) as SVGSVGElement;

        // Make the SVG fully self-contained for standalone/PDF rendering
        clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clonedSvg.setAttribute('width', String(w));
        clonedSvg.setAttribute('height', String(h));
        if (!clonedSvg.getAttribute('viewBox')) {
          clonedSvg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        }

        // Strip inline style entirely — Recharts sets style="width:100%;height:100%"
        // which collapses to zero outside a sized container
        clonedSvg.removeAttribute('style');

        // Inline computed styles on all elements (fills, fonts, transforms)
        // so the SVG renders correctly outside the app's CSS context
        inlineComputedStyles(svg, clonedSvg);

        return clonedSvg.outerHTML;
      },
    }));

    const renderChart = () => {
      const extras = { isAnimationActive, referenceLines, onMark, resizeDebounceMs };
      switch (config.type) {
        case 'bar':
          return <BarChartView data={data} config={config} height={height} {...extras} />;
        case 'line':
          return <LineChartView data={data} config={config} height={height} {...extras} />;
        case 'pie':
          return <PieChartView data={data} config={config} height={height} isAnimationActive={isAnimationActive} onMark={onMark} resizeDebounceMs={resizeDebounceMs} />;
        case 'area':
          return <AreaChartView data={data} config={config} height={height} {...extras} />;
        case 'scatter':
          return <ScatterChartView data={data} config={config} height={height} {...extras} />;
        default:
          return (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Unknown chart type: {config.type}
            </div>
          );
      }
    };

    return (
      <div ref={containerRef} className={className} style={{ height }}>
        {renderChart()}
      </div>
    );
  }
);
